import { Checkbox, TableCell, TableHead, TableRow, TableSortLabel } from '@mui/material'

import type { DataTableSelection } from '..'
import { columnDividerSx } from '../columnDivider'

export interface ITableColumn<T> {
  key?: keyof T | string
  id: keyof T | string
  label: string
  // Groups adjacent columns under one spanning header cell (a two-tier header).
  // Contiguous columns sharing the same group string sit beneath it.
  group?: string
  numeric?: boolean
  align?: 'right' | 'left' | 'center'
  minWidth?: string
  // A fixed width (px or %). When any column on a table declares one, the table
  // switches to fixed layout and stops growing to fit its content — long values
  // wrap inside their column instead of pushing the page into a sideways scroll.
  width?: string
  disablePadding?: boolean
  enableSorting?: boolean
  format?: (value: unknown) => string
  render?: (value: any, row: T) => React.ReactNode
}

interface TableHeadProps<T> {
  rowCount?: number
  columns: ITableColumn<T>[]
  order: 'asc' | 'desc'
  orderBy: string
  onRequestSort: (property: keyof T) => void
  visibleRows: T[]
  showNestedContentIndicator?: boolean
  selection?: DataTableSelection<T>
}

export default function Component<T extends object>({
  columns,
  order,
  orderBy,
  onRequestSort,
  visibleRows,
  showNestedContentIndicator,
  selection,
}: TableHeadProps<T>) {
  const cellSx = (headCell: ITableColumn<T>, index: number) => ({
    fontSize: '14px',
    padding: '8px 12px',
    minWidth: headCell.width
      ? undefined
      : headCell.minWidth && visibleRows?.length > 0
        ? headCell.minWidth
        : '80px',
    width: headCell.width,
    paddingLeft: index === 0 ? '24px' : undefined,
    fontWeight: 600,
    color: '#344054',
    ...columnDividerSx('#D0D5DD'),
  })

  const label = (headCell: ITableColumn<T>) =>
    headCell.enableSorting ? (
      <TableSortLabel
        active={orderBy === headCell.id}
        direction={orderBy === headCell.id ? order : 'asc'}
        onClick={() => {
          // Checked across all rows, not just the first: an optional field can
          // be absent from row zero while present further down, and testing
          // only row zero left such a column's sort control inert.
          if (visibleRows?.some(row => headCell.id in row)) {
            onRequestSort(headCell.id as keyof T)
          }
        }}
      >
        {headCell.label}
      </TableSortLabel>
    ) : (
      <span>{headCell.label}</span>
    )

  const headCell = (col: ITableColumn<T>, index: number, rowSpan?: number) => (
    <TableCell
      key={String(col.id)}
      align={col.align ?? 'left'}
      rowSpan={rowSpan}
      padding={col.disablePadding ? 'none' : 'normal'}
      sx={cellSx(col, index)}
    >
      {label(col)}
    </TableCell>
  )

  if (!columns.some(c => c.group)) {
    return (
      <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
        <TableRow>
          {selection && <SelectAllCell selection={selection} visibleRows={visibleRows} />}
          {showNestedContentIndicator && <ExpandHeaderCell />}
          {columns.map((col, index) => headCell(col, index))}
        </TableRow>
      </TableHead>
    )
  }

  // Two-tier header: ungrouped columns span both rows; each contiguous run of
  // same-group columns gets one spanning cell above their individual labels.
  const topRow: React.ReactNode[] = []
  columns.forEach((col, index) => {
    if (!col.group) {
      topRow.push(headCell(col, index, 2))
      return
    }
    const isRunStart = index === 0 || columns[index - 1].group !== col.group
    if (!isRunStart) return
    let span = 0
    while (index + span < columns.length && columns[index + span].group === col.group) span++
    topRow.push(
      <TableCell
        key={`group-${col.group}-${index}`}
        colSpan={span}
        align="center"
        sx={{ fontSize: '14px', padding: 1, fontWeight: 600 }}
      >
        {col.group}
      </TableCell>
    )
  })

  return (
    <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
      <TableRow>
        {selection && <SelectAllCell selection={selection} visibleRows={visibleRows} rowSpan={2} />}
        {showNestedContentIndicator && <ExpandHeaderCell rowSpan={2} />}
        {topRow}
      </TableRow>
      <TableRow>{columns.map((col, index) => (col.group ? headCell(col, index) : null))}</TableRow>
    </TableHead>
  )
}

function ExpandHeaderCell({ rowSpan }: { rowSpan?: number }) {
  return (
    <TableCell
      rowSpan={rowSpan}
      sx={{
        padding: '8px 0 8px 16px',
        width: '40px',
      }}
    />
  )
}

function SelectAllCell<T>({
  selection,
  visibleRows,
  rowSpan,
}: {
  selection: DataTableSelection<T>
  visibleRows: T[]
  rowSpan?: number
}) {
  const ids = (visibleRows ?? []).map(selection.getRowId).filter(Boolean)
  const selectedCount = ids.filter(id => selection.selected.has(id)).length
  const allSelected = ids.length > 0 && selectedCount === ids.length

  // Only the rows on screen are added or removed; a selection made on another page is left alone.
  const toggleAll = () => {
    const next = new Set(selection.selected)
    ids.forEach(id => (allSelected ? next.delete(id) : next.add(id)))
    selection.onChange(next)
  }

  return (
    <TableCell rowSpan={rowSpan} padding="checkbox" sx={{ paddingLeft: '12px' }}>
      <Checkbox
        size="small"
        disabled={ids.length === 0}
        checked={allSelected}
        indeterminate={selectedCount > 0 && !allSelected}
        onChange={toggleAll}
        inputProps={{ 'aria-label': 'Select all rows on this page' }}
      />
    </TableCell>
  )
}
